// src/components/about/DomainDiagram.tsx
// Abstract, monochrome line diagrams — one per engineering domain.
// Deliberately schematic rather than illustrative: they read as system
// shapes, not decoration. All strokes inherit `currentColor`.

import { AboutDomainDiagram as DiagramKey } from '../../config/aboutConfig';

interface DomainDiagramProps {
  variant: DiagramKey;
}

const strokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const FlightPath = () => (
  <>
    <path d="M8 50 C 24 50 30 12 64 12" {...strokeProps} strokeDasharray="3 4" />
    <circle cx="8" cy="50" r="3.5" fill="currentColor" />
    <circle cx="64" cy="12" r="3.5" fill="currentColor" />
    <path d="M36 31 l5 5 -5 5 -5 -5 z" {...strokeProps} />
  </>
);

const Mesh = () => (
  <>
    <path d="M36 30 L12 12 M36 30 L60 12 M36 30 L12 48 M36 30 L60 48" {...strokeProps} />
    <circle cx="36" cy="30" r="5" fill="currentColor" />
    <circle cx="12" cy="12" r="3" {...strokeProps} />
    <circle cx="60" cy="12" r="3" {...strokeProps} />
    <circle cx="12" cy="48" r="3" {...strokeProps} />
    <circle cx="60" cy="48" r="3" {...strokeProps} />
  </>
);

const Hierarchy = () => (
  <>
    <rect x="30" y="5" width="12" height="9" rx="1.5" {...strokeProps} />
    <path d="M36 14 L36 21 M18 21 L54 21 M18 21 L18 28 M54 21 L54 28" {...strokeProps} />
    <rect x="12" y="28" width="12" height="9" rx="1.5" {...strokeProps} />
    <rect x="48" y="28" width="12" height="9" rx="1.5" {...strokeProps} />
    <path d="M54 37 L54 46 L36 46 L36 51" {...strokeProps} />
    <rect x="30" y="51" width="12" height="9" rx="1.5" {...strokeProps} />
  </>
);

const Pipeline = () => (
  <>
    <rect x="3" y="8" width="17" height="13" rx="1.5" {...strokeProps} />
    <rect x="27.5" y="8" width="17" height="13" rx="1.5" {...strokeProps} />
    <rect x="52" y="8" width="17" height="13" rx="1.5" {...strokeProps} />
    <path d="M20 14.5 L26 14.5 M23 12 L26 14.5 L23 17" {...strokeProps} />
    <path d="M44.5 14.5 L50.5 14.5 M47.5 12 L50.5 14.5 L47.5 17" {...strokeProps} />
    <path d="M11.5 21 L11.5 32 M36 21 L36 32 M60.5 21 L60.5 32 M11.5 32 L60.5 32" {...strokeProps} />
    <path d="M36 32 L36 43" {...strokeProps} />
    <rect x="27.5" y="43" width="17" height="12" rx="1.5" fill="currentColor" />
  </>
);

const DomainDiagram = ({ variant }: DomainDiagramProps) => {
  return (
    <svg viewBox="0 0 72 62" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      {variant === 'flightPath' && <FlightPath />}
      {variant === 'mesh' && <Mesh />}
      {variant === 'hierarchy' && <Hierarchy />}
      {variant === 'pipeline' && <Pipeline />}
    </svg>
  );
};

export default DomainDiagram;
